import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  CheckIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  XIcon,
} from '../../../../icons/animated';
import { Button } from '../Buttons';

interface FilterOption {
  label: string;
  key: string;
  type?: 'checkbox' | 'range'; // Added 'range' type for range filters
  options?: string[]; // Only needed for checkbox options
  min?: number; // For range filters
  max?: number; // For range filters
  // For custom components
 // Indicates if custom filters are applied
}

interface FilterSearchProps {
  filterOptions?: FilterOption[];
  onSearch: (query: string) => void;
  onApplyFilters?: (filters: { [key: string]: any }) => void;
  searchText?: string;
  children?: React.ReactNode;
  hasChildren?: boolean;
  resetFilters?: () => void; // Optional prop to reset filters
  hasFilters?: boolean;
}

export const FilterSearch: React.FC<FilterSearchProps> = ({
  filterOptions,
  onSearch,
  onApplyFilters,
  children,
  searchText,
  hasChildren,
  resetFilters,
  hasFilters = true,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: any;
  }>({});
  const [rangeValues, setRangeValues] = useState<{
    [key: string]: { min: number; max: number };
  }>({});

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch(value); // ✅ Syncs jobTitle immediately
  };

  const handleClearFilters = () => {
    // Reset selected filters and range values to empty/default
    setSelectedFilters({});
    setRangeValues({});
    resetFilters?.(); // Call the resetFilters prop if provided
    onApplyFilters?.({}); // Notify parent about cleared filters
  };

  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Handle click outside to close filter
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    // Handle scroll lock
    const handleScrollLock = () => {
      if (isFilterOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      handleScrollLock();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isFilterOpen]);

  const handleFilterToggle = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleApplyFilters = () => {
    const filters = { ...selectedFilters };

    // Only include range values if they are actually set
    Object.keys(rangeValues).forEach((key) => {
      if (
        rangeValues[key]?.min !== undefined ||
        rangeValues[key]?.max !== undefined
      ) {
        filters[key] = rangeValues[key];
      }
    });

    onApplyFilters?.(filters);
    setIsFilterOpen(false);
  };

  const handleCancelFilters = () => {
    // Just close the filter without applying changes
    setIsFilterOpen(false);
  };

  const handleCheckboxChange = (filterKey: string, option: string) => {
    setSelectedFilters((prev) => {
      const updatedFilters = { ...prev };
      if (updatedFilters[filterKey]?.includes(option)) {
        updatedFilters[filterKey] = updatedFilters[filterKey].filter(
          (item: string) => item !== option,
        );
      } else {
        updatedFilters[filterKey] = [
          ...(updatedFilters[filterKey] || []),
          option,
        ];
      }
      if (updatedFilters[filterKey].length === 0)
        delete updatedFilters[filterKey];
      return updatedFilters;
    });
  };

  const handleRangeChange = (
    filterKey: string,
    value: string,
    isMin: boolean,
  ) => {
    setRangeValues((prev) => {
      const updatedRange = { ...prev };
      updatedRange[filterKey] = {
        ...(updatedRange[filterKey] || {}),
        [isMin ? 'min' : 'max']: parseInt(value, 10),
      } as any;
      return updatedRange;
    });
  };

  const handleSelectAll = (filterKey: string, options: string[]) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterKey]:
        prev[filterKey]?.length === options.length ? [] : [...options],
    }));
  };

  const safeFilterOptions = Array.isArray(filterOptions) ? filterOptions : [];
  const [activeTab, setActiveTab] = useState<string>(
    safeFilterOptions[0]?.key || '',
  );

  return (
    <div
        className='relative flex w-full max-w-lg flex-col items-start gap-2 sm:space-y-3'
        ref={filterRef}
    >
      { /* Search Box */ }
      <div className='flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3'>
        <div className='relative flex-1'>
          <div className='relative group w-full'>
            <SearchIcon size={18} className='absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 transition-colors duration-200 group-hover:text-emerald-500 sm:left-4' />
            <input
                type='text'
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    onSearch(searchQuery);
                  }
                }}
                placeholder={searchText ?? 'Search...'}
                className='min-h-9 w-full rounded-lg border-2 border-gray-100 bg-white py-1.5 pl-9 pr-3 text-sm text-gray-600 sm:min-h-10 sm:rounded-2xl sm:py-2 sm:pl-12 sm:pr-4
                       placeholder:text-gray-400 outline-none transition-all duration-200
                       hover:border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100
                       shadow-sm hover:shadow-sm'
            />
          </div>
        </div>
        { hasFilters && (
          <Button
              label='Filter'
              onClick={handleFilterToggle}
              variant='secondary'
              hasIcon
              className='w-full sm:w-auto bg-white border-2 border-gray-100 hover:border-gray-200 rounded-lg sm:rounded-2xl px-3 py-1.5 sm:px-4 sm:py-2
                      text-gray-700 font-medium  duration-200
                      hover:bg-gray-50 hover:shadow-sm focus:outline-none focus:ring-1
                      focus:ring-emerald-100 flex items-center gap-2'
              icon={
                <div className='relative'>
                  <SlidersHorizontalIcon size={18} className='text-gray-500' />
                  { Object.keys(selectedFilters).length > 0 && (
                    <span className='absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full'></span>
                  ) }
                </div>
              }
          />
        ) }
        { /* Filter Button */ }
      </div>

      { /* Filter Popup */ }
      { isFilterOpen && (
        <div className='fixed inset-x-3 top-20 bottom-3 sm:absolute sm:inset-auto sm:top-full sm:left-0 sm:max-w-xl sm:mt-2 sm:w-full bg-white rounded-lg sm:rounded-2xl shadow-xl z-50 border border-gray-100 overflow-hidden transform transition-all duration-200 flex flex-col'>
          <div className='flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0'>
            <h2 className='text-base sm:text-lg font-semibold text-gray-800'>Filters</h2>
            <span className='px-3 py-1 text-sm bg-emerald-50 text-emerald-600 rounded-full font-medium'>
              { Object.keys(selectedFilters).length } Active
            </span>
          </div>

          { hasChildren ? (
            <div className='flex-1 overflow-y-auto' style={{ maxHeight: '450px' }}>{ children }</div>
          ) : (
            <div className='flex flex-1 min-h-0 overflow-hidden'>
              { /* Left-side panel (Filter Labels as Tabs) */ }
              <div className='w-[42%] sm:w-1/3 bg-gray-50 border-r border-gray-100 overflow-y-auto'>
                { safeFilterOptions.length > 0 ? (
                  <div className='flex flex-col py-2'>
                    { safeFilterOptions.map((filter: any) => (
                      <button
                          key={filter.key}
                          onClick={() => setActiveTab(filter.key)}
                          className={`px-3 py-3 sm:px-6 text-left text-sm font-medium transition-all duration-200
                          ${
                            activeTab === filter.key
                              ? 'text-emerald-600 bg-white border-l-4 border-emerald-500'
                              : 'text-gray-600 hover:bg-gray-100 border-l-4 border-transparent'
                          }`}
                      >
                        { filter.label }
                      </button>
                    )) }
                  </div>
                ) : (
                  <div className='flex flex-col items-center justify-center h-full p-6 text-center'>
                    <SlidersHorizontalIcon size={36} className='text-gray-300 mb-2' />
                    <p className='text-gray-500'>
                      No filter categories available
                    </p>
                  </div>
                ) }
              </div>

              { /* Right-side panel */ }
              <div className='flex-1 flex flex-col'>
                <div className='flex-1 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4'>
                  { safeFilterOptions.map(
                    (filter: any) =>
                      activeTab === filter.key && (
                        <div key={filter.key} className='space-y-4'>
                          { /* Checkbox Filters */ }
                          { filter.type === 'checkbox' && (
                            <div className='space-y-3'>
                              <div className='flex items-center justify-between px-2'>
                                <div className='flex items-center gap-2'>
                                  <input
                                      type='checkbox'
                                      checked={
                                        selectedFilters[filter.key]?.length ===
                                      filter.options.length
                                      }
                                      onChange={() =>
                                        handleSelectAll(
                                          filter.key,
                                          filter.options,
                                        )
                                      }
                                      className='w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500'
                                  />
                                  <span className='text-sm font-medium text-gray-700'>
                                    Select All
                                  </span>
                                </div>
                                <span className='text-xs text-gray-500'>
                                  { selectedFilters[filter.key]?.length || 0 }/
                                  { filter.options.length }
                                </span>
                              </div>

                              <div className='space-y-2'>
                                { filter.options.map((option: string) => (
                                  <label
                                      key={option}
                                      className='flex items-center px-2 py-1.5 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors duration-150'
                                  >
                                    <input
                                        type='checkbox'
                                        checked={selectedFilters[
                                          filter.key
                                        ]?.includes(option)}
                                        onChange={() =>
                                          handleCheckboxChange(filter.key, option)
                                        }
                                        className='w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500'
                                    />
                                    <span className='ml-2 text-sm text-gray-600'>
                                      { option }
                                    </span>
                                  </label>
                                )) }
                              </div>
                            </div>
                          ) }

                          { /* Range Filter */ }
                          { filter.type === 'range' && (
                            <div className='space-y-4'>
                              <h3 className='text-sm font-medium text-gray-700'>
                                Range Selection
                              </h3>
                              <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4'>
                                <div className='flex-1'>
                                  <input
                                      type='number'
                                      placeholder='Min'
                                      value={
                                        rangeValues[filter.key]?.min || filter.min
                                      }
                                      onChange={(e) =>
                                        handleRangeChange(
                                          filter.key,
                                          e.target.value,
                                          true,
                                        )
                                      }
                                      className='w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 transition-all duration-200'
                                  />
                                </div>
                                <span className='text-gray-400'>to</span>
                                <div className='flex-1'>
                                  <input
                                      type='number'
                                      placeholder='Max'
                                      value={
                                        rangeValues[filter.key]?.max || filter.max
                                      }
                                      onChange={(e) =>
                                        handleRangeChange(
                                          filter.key,
                                          e.target.value,
                                          false,
                                        )
                                      }
                                      className='w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 transition-all duration-200'
                                  />
                                </div>
                              </div>
                            </div>
                          ) }

                          { /* No Options Message */ }
                          { (!filter.options || filter.options.length === 0) && (
                            <div className='flex flex-col items-center justify-center py-8 text-center'>
                              <SlidersHorizontalIcon size={36} className='text-gray-200 mb-3' />
                              <p className='text-gray-500 text-sm'>
                                No filter options available
                              </p>
                            </div>
                          ) }
                        </div>
                      ),
                  ) }
                </div>

                { /* Action Buttons */ }
              </div>
            </div>
          ) }
          <div className='border-t border-gray-100 p-3 sm:p-4 bg-gray-50 flex-shrink-0'>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
              <div className='flex flex-wrap items-center gap-2'>
                <button
                    onClick={handleClearFilters}
                    className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900
                                 bg-white border border-gray-200 rounded-lg hover:bg-gray-50
                                 transition-all duration-200 group'
                    title='Reset all filters to default state'
                >
                  <SlidersHorizontalIcon size={16} className='mr-1.5 text-gray-400 group-hover:text-gray-600' />
                  Clear all
                </button>
              </div>
              <div className='flex items-center gap-2'>
                <button
                    onClick={handleCancelFilters}
                    className='inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600
                                 hover:text-gray-800 hover:bg-gray-100 rounded-lg
                                 transition-colors duration-200'
                    title='Close without applying changes'
                >
                  <XIcon size={16} className='mr-1' />
                  Cancel
                </button>
                <button
                    onClick={handleApplyFilters}
                    className='inline-flex items-center px-4 py-2 text-sm font-medium text-white
                                 bg-emerald-500 rounded-lg hover:bg-emerald-600
                                 transition-all duration-200 shadow-sm hover:shadow
                                 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1'
                    title='Save and apply selected filters'
                >
                  <CheckIcon size={16} className='mr-1.5 text-white' />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      ) }
    </div>
  );
};
